import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useAuth } from '../contexts/AuthContext'
import { useNotification } from '../contexts/NotificationContext'
import { documentService } from '../services/documentService'
import type { Document } from '../types/document'
import {
  ArrowLeftIcon,
  PencilIcon,
  TrashIcon,
  DocumentArrowDownIcon,
  PrinterIcon,
  EyeIcon,
  CalendarIcon,
  TagIcon,
  UserGroupIcon,
  BuildingOfficeIcon,
  ShieldCheckIcon,
  CameraIcon,
  MapPinIcon,
  BriefcaseIcon,
  DocumentDuplicateIcon
} from '@heroicons/react/24/outline'
import AppHeader from '../components/AppHeader'
import ProfileDropdown from '../components/ProfileDropdown'
import NotificationDropdown from '../components/NotificationDropdown'
import SettingsDropdown from '../components/SettingsDropdown'

const DocumentViewPage = () => {
  const { id } = useParams<{ id: string }>()
  const { user } = useAuth()
  const { showSuccess, showError } = useNotification()
  const navigate = useNavigate()

  const [document, setDocument] = useState<Document | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  useEffect(() => {
    if (user && id) {
      loadDocument()
    }
  }, [user, id])

  const loadDocument = async () => {
    if (!user || !id) return

    setIsLoading(true)
    const result = await documentService.getDocument(id, user.id)

    if (result.success && result.document) {
      setDocument(result.document)
    } else {
      showError('Error', result.error || 'Failed to load document')
      navigate('/documents')
    }
    setIsLoading(false)
  }

  const handleDelete = async () => {
    if (!user || !id) return

    setIsDeleting(true)
    const result = await documentService.deleteDocument(id, user.id)

    if (result.success) {
      showSuccess('Success', 'Document deleted successfully')
      navigate('/documents')
    } else {
      showError('Error', result.error || 'Failed to delete document')
    }
    setIsDeleting(false)
    setShowDeleteConfirm(false)
  }

  const handleDownloadPDF = () => {
    if (!document) return

    const htmlContent = documentService.generatePDFContent(document)
    
    // Create a new window with the PDF content
    const printWindow = window.open('', '_blank')
    if (printWindow) {
      printWindow.document.write(htmlContent)
      printWindow.document.close()
      
      // Trigger print dialog
      printWindow.onload = () => {
        printWindow.print()
      }
    }
  }

  const handlePrint = () => {
    if (!document) return
    
    const htmlContent = documentService.generatePDFContent(document)
    const printWindow = window.open('', '_blank')
    
    if (printWindow) {
      printWindow.document.write(htmlContent)
      printWindow.document.close()
      printWindow.onload = () => {
        printWindow.print()
        printWindow.close()
      }
    }
  }

  const getDocumentTypeIcon = (type: string) => {
    switch (type) {
      case 'subcontractor_agreement':
      case 'independent_contractor':
        return <BriefcaseIcon className="w-6 h-6" />
      case 'nda':
        return <ShieldCheckIcon className="w-6 h-6" />
      case 'client_contract':
      case 'service_agreement':
        return <DocumentDuplicateIcon className="w-6 h-6" />
      case 'model_release':
        return <CameraIcon className="w-6 h-6" />
      case 'location_release':
        return <MapPinIcon className="w-6 h-6" />
      case 'vendor_agreement':
        return <BuildingOfficeIcon className="w-6 h-6" />
      case 'employment_contract':
        return <UserGroupIcon className="w-6 h-6" />
      default:
        return <DocumentDuplicateIcon className="w-6 h-6" />
    }
  }

  const formatDocumentType = (type: string) => {
    return type.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ')
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  if (isLoading) {
    return (
      <div className="min-h-screen relative overflow-hidden">
        <div className="fixed inset-0 overflow-hidden pointer-events-none">
          <div className="floating-orb floating-orb-1"></div>
          <div className="floating-orb floating-orb-2"></div>
          <div className="floating-orb floating-orb-3"></div>
          <div className="floating-orb floating-orb-4"></div>
          <div className="floating-orb floating-orb-5"></div>
        </div>
        
        <AppHeader>
          <NotificationDropdown />
          <SettingsDropdown />
          <ProfileDropdown />
        </AppHeader>

        <main className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
            <p className="text-gray-400">Loading document...</p>
          </div>
        </main>
      </div>
    )
  }

  if (!document) {
    return (
      <div className="min-h-screen relative overflow-hidden">
        <div className="fixed inset-0 overflow-hidden pointer-events-none">
          <div className="floating-orb floating-orb-1"></div>
          <div className="floating-orb floating-orb-2"></div>
          <div className="floating-orb floating-orb-3"></div>
          <div className="floating-orb floating-orb-4"></div>
          <div className="floating-orb floating-orb-5"></div>
        </div>
        
        <AppHeader>
          <NotificationDropdown />
          <SettingsDropdown />
          <ProfileDropdown />
        </AppHeader>

        <main className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center py-12">
            <DocumentDuplicateIcon className="w-16 h-16 text-gray-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-white mb-2">Document Not Found</h2>
            <p className="text-gray-400 mb-6">The document you're looking for doesn't exist or you don't have permission to view it.</p>
            <Link
              to="/documents"
              className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-lg font-medium transition-colors"
            >
              Back to Documents
            </Link>
          </div>
        </main>
      </div>
    )
  }

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Floating orbs for glassmorphism background effect */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="floating-orb floating-orb-1"></div>
        <div className="floating-orb floating-orb-2"></div>
        <div className="floating-orb floating-orb-3"></div>
        <div className="floating-orb floating-orb-4"></div>
        <div className="floating-orb floating-orb-5"></div>
      </div>

      {/* Header */}
      <AppHeader>
        <NotificationDropdown />
        <SettingsDropdown />
        <ProfileDropdown />
      </AppHeader>

      {/* Main Content */}
      <main className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between mb-8"
        >
          <div className="flex items-center space-x-4">
            <Link
              to="/documents"
              className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
            >
              <ArrowLeftIcon className="w-5 h-5 text-white" />
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-white">View Document</h1>
              <p className="text-gray-400">Review and manage your document</p>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <button
              onClick={handlePrint}
              className="p-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
              title="Print Document"
            >
              <PrinterIcon className="w-5 h-5" />
            </button>
            <button
              onClick={handleDownloadPDF}
              className="p-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors"
              title="Download PDF"
            >
              <DocumentArrowDownIcon className="w-5 h-5" />
            </button>
            <Link
              to={`/documents/${document.id}/edit`}
              className="p-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors"
              title="Edit Document"
            >
              <PencilIcon className="w-5 h-5" />
            </Link>
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="p-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors"
              title="Delete Document"
            >
              <TrashIcon className="w-5 h-5" />
            </button>
          </div>
        </motion.div>

        {/* Document Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="glass-mockup rounded-xl p-6 mb-8"
        >
          <div className="flex items-start space-x-4">
            <div className="p-3 bg-blue-500/20 rounded-lg">
              {getDocumentTypeIcon(document.documentType)}
            </div>
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-white mb-2">{document.title}</h2>
              <p className="text-gray-400 mb-4">{formatDocumentType(document.documentType)} • {document.category}</p>
              
              {document.description && (
                <p className="text-gray-300 mb-4">{document.description}</p>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div className="flex items-center space-x-2 text-gray-400">
                  <CalendarIcon className="w-4 h-4" />
                  <span>Created: {formatDate(document.createdAt)}</span>
                </div>
                {document.effectiveDate && (
                  <div className="flex items-center space-x-2 text-gray-400">
                    <CalendarIcon className="w-4 h-4" />
                    <span>Effective: {formatDate(document.effectiveDate)}</span>
                  </div>
                )}
                {document.expirationDate && (
                  <div className="flex items-center space-x-2 text-gray-400">
                    <CalendarIcon className="w-4 h-4" />
                    <span>Expires: {formatDate(document.expirationDate)}</span>
                  </div>
                )}
                {document.contractValue && (
                  <div className="flex items-center space-x-2 text-gray-400">
                    <span>Value: ${document.contractValue.toLocaleString()}</span>
                  </div>
                )}
              </div>

              {document.tags.length > 0 && (
                <div className="flex items-center space-x-2 mt-4">
                  <TagIcon className="w-4 h-4 text-gray-400" />
                  <div className="flex flex-wrap gap-2">
                    {document.tags.map((tag, index) => (
                      <span
                        key={index}
                        className="px-2 py-1 bg-blue-500/20 text-blue-300 text-xs rounded-full"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </motion.div>

        {/* Parties Section */}
        {document.parties.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="glass-mockup rounded-xl p-6 mb-8"
          >
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
              <UserGroupIcon className="w-5 h-5" />
              <span>Document Parties</span>
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {document.parties.map((party) => (
                <div key={party.id} className="bg-white/5 rounded-lg p-4">
                  <h4 className="font-medium text-white">{party.name}</h4>
                  <p className="text-sm text-gray-400">{party.role}</p>
                  <p className="text-sm text-gray-400">{party.email}</p>
                  {party.company && <p className="text-sm text-gray-400">{party.company}</p>}
                  <div className="mt-2 flex items-center space-x-2">
                    <div className={`w-2 h-2 rounded-full ${party.signed ? 'bg-green-400' : 'bg-yellow-400'}`}></div>
                    <span className="text-xs text-gray-400">
                      {party.signed ? 'Signed' : 'Awaiting Signature'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Document Content */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="glass-mockup rounded-xl p-6"
        >
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
            <EyeIcon className="w-5 h-5" />
            <span>Document Content</span>
          </h3>
          <div 
            className="prose prose-invert max-w-none bg-white/5 rounded-lg p-6"
            dangerouslySetInnerHTML={{ __html: document.content || 'No content available' }}
          />
        </motion.div>
      </main>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="glass-mockup rounded-xl p-6 max-w-md w-full"
          >
            <h3 className="text-xl font-bold text-white mb-4">Delete Document</h3>
            <p className="text-gray-300 mb-6">
              Are you sure you want to delete "{document.title}"? This action cannot be undone.
            </p>
            <div className="flex space-x-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={isDeleting}
                className="flex-1 px-4 py-2 bg-red-500 hover:bg-red-600 disabled:bg-red-600/50 text-white rounded-lg transition-colors flex items-center justify-center space-x-2"
              >
                {isDeleting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Deleting...</span>
                  </>
                ) : (
                  <>
                    <TrashIcon className="w-4 h-4" />
                    <span>Delete</span>
                  </>
                )}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  )
}

export default DocumentViewPage 